import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import DynamicTextIcon from '../../../assets/icons/dynamic-text.svg';
import PrimaryModal from '../Modal/PrimaryModal';
import { PrimaryButton } from "..";
import infoIcon from '../../../assets/icons/red-caution.svg';
import { useContentfulContext } from '../../../contexts/ContentfulContext';
import InlineTooltip from '../Tooltip/InlineTooltip';

const DynamicTextType = ({ value, id, label, handleChange, mandatory, element, helpText }) => {
    const [showEditor, setShowEditor] = useState(false);
    const [initialValue, setInitialValue] = useState(value || '');
    const { content } = useContentfulContext();

    const formik = useFormik({
        initialValues: {
            inputFieldValue: initialValue,
        },
        validationSchema: Yup.object({
            inputFieldValue: Yup.string()
                .required(`${label} is required.`)
        }),
        onSubmit: (values) => {
            handleChange({ target: { name: id, value: values.inputFieldValue } });
            setInitialValue(values.inputFieldValue);
            setShowEditor(false);
        },
    });

    const handleOpenModal = () => {
        setShowEditor(true);
    };

    const handleHideModal = () => {
        setShowEditor(false);
        formik.resetForm({ values: { inputFieldValue: initialValue } });
    };

    useEffect(() => {
        setInitialValue(value);
        formik.setFieldValue('inputFieldValue', value);
    }, [value]);

    return (
        <div className='mb-4'>
            <div className="relative" dzt-field-id={id}>
                <input
                    disabled 
                    type="text" 
                    name={id} 
                    className="block px-2.5 pb-2.5 pt-4 w-full pr-10 text-sm text-gray-900 bg-transparent rounded-lg border border-gray-2 appearance-none focus:outline-none focus:ring-1 focus:ring-primary-2 ring-primary-2 peer" 
                    placeholder=" " 
                    value={element.input.input_default_value} 
                    title={element.input.input_default_value}
                    style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                />
                <label
                    htmlFor={id}
                    className="absolute text-sm text-black -translate-y-4 scale-75 top-2 z-10 origin-[0]
                                bg-gray-50 px-2 left-1 flex items-center gap-1
                                peer-focus:text-primary"
                >
                    <span>
                        {label} {mandatory && '*'}
                    </span>
                    {helpText && (
                        <InlineTooltip
                            content={helpText}
                            imgClass="pt-0.5"
                        />
                    )}
                </label>
                <img 
                    alt="Icon"
                    src={DynamicTextIcon} 
                    className='absolute right-2 sm:right-6 top-1/2 transform -translate-y-1/2 h-[18px] w-[18px] cursor-pointer'
                    onClick={handleOpenModal}
                />
            </div>

            {showEditor && (
                <PrimaryModal title={label} hideModal={handleHideModal}>
                    <div className='p-6 space-y-6'>
                        <div className='flex items-start gap-2 text-red-5'>
                            <img src={infoIcon} alt="" className='w-4 mt-0.5'/>
                            <p className='mb-4'>{content.INPUT_DYNAMIC_TEXT_CONTENT.message}</p>
                        </div>
                        <form onSubmit={formik.handleSubmit}>
                            <div className="relative mb-2" dzt-field-id={id}>
                                <input
                                    type="text" 
                                    name="inputFieldValue" 
                                    className="block px-2.5 pb-2.5 pt-4 w-full pr-10 text-sm text-gray-900 bg-transparent rounded-lg border border-gray-2 appearance-none focus:outline-none focus:ring-1 focus:ring-primary-2 ring-primary peer" 
                                    placeholder=" " 
                                    value={formik.values.inputFieldValue} 
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                />
                                <label 
                                    htmlFor="inputFieldValue" 
                                    className="absolute text-sm text-black duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-gray-50 px-2 peer-focus:px-2 peer-focus:text-primary peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 left-1"
                                >
                                    { label } {mandatory && '*'}
                                </label>
                            </div>
                            <div className="min-h-[24px]">
                                {formik.touched.inputFieldValue && formik.errors.inputFieldValue && (
                                    <p className="mt-2 text-sm text-red-500">{formik.errors.inputFieldValue}</p>
                                )}
                            </div>
                            <div className="flex items-center justify-end space-x-2">
                                <PrimaryButton type="submit">{content.INPUT_DYNAMIC_TEXT_CONTENT.save_btn}</PrimaryButton>
                            </div>
                        </form>
                    </div>
                </PrimaryModal>
            )}
        </div>
    );
};

export default DynamicTextType;
